import { FileText, Image, Paperclip, X } from "lucide-react"

import { cn } from "@/lib/utils"

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function getFileTypeLabel(contentType: string): string {
  if (contentType === "application/pdf") return "PDF"
  if (contentType.startsWith("image/"))
    return contentType.split("/")[1].toUpperCase()
  return "File"
}

function AttachmentIcon({ contentType }: { contentType: string }) {
  if (contentType === "application/pdf")
    return <FileText className="size-5 shrink-0" />
  if (contentType.startsWith("image/"))
    return <Image className="size-5 shrink-0" />
  return <Paperclip className="size-5 shrink-0" />
}

interface BaseProps {
  filename: string
  fileSize: number
  contentType: string
}

interface LinkChipProps extends BaseProps {
  href: string
  onRemove?: never
}

interface RemovableChipProps extends BaseProps {
  onRemove: () => void
  href?: never
}

type AttachmentChipProps = LinkChipProps | RemovableChipProps

export function AttachmentChip({
  filename,
  fileSize: _fileSize,
  contentType,
  href,
  onRemove,
}: AttachmentChipProps) {
  const cardClass = cn(
    "bg-card flex w-64 items-center gap-3 rounded-2xl border px-3 py-2.5",
  )

  const inner = (
    <>
      <div className="bg-muted flex size-10 shrink-0 items-center justify-center rounded-xl">
        <AttachmentIcon contentType={contentType} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm leading-tight font-medium">{filename}</p>
        <p className="text-muted-foreground text-xs">
          {getFileTypeLabel(contentType)}
        </p>
      </div>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="text-muted-foreground hover:text-foreground shrink-0 transition-colors"
        >
          <X className="size-4" />
        </button>
      )}
    </>
  )

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(cardClass, "hover:bg-accent transition-colors")}
      >
        {inner}
      </a>
    )
  }

  return <div className={cardClass}>{inner}</div>
}
